import { useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Button } from "./Button";
import { FieldWrapper } from "./FieldWrapper";

interface Props {
  open: boolean;
  /** Valor inicial; se re-sincroniza cada vez que el modal se abre. */
  initialValue: string;
  /**
   * Se ejecuta al presionar "Editar" con el RFC ya normalizado (trim + mayúsculas).
   * Vive en el padre: ahí se hace el patch al API y la actualización del store. Si
   * regresa una promesa, el modal deshabilita sus controles mientras se resuelve.
   */
  onSubmit: (rfc: string) => void | Promise<void>;
  onClose: () => void;
  /** Deshabilita los controles por estado del padre (ej. el CIEC ya fue validado). */
  disabled: boolean;
  /** Largo máximo del RFC: 12 para PM, 13 para PFAE. */
  maxLength: number;
  /** Formato esperado según el régimen (RFC_PM / RFC_PFAE). */
  pattern: RegExp;
  /**
   * Error devuelto por el API tras un intento fallido (ej. RFC ya registrado). Se muestra bajo el
   * campo y se limpia en cuanto el usuario vuelve a escribir. El padre debe reiniciarlo antes de
   * cada envío para que un mismo error consecutivo se vuelva a mostrar.
   */
  error: string;
  title: string;
  fieldLabel: string;
  /** Texto auxiliar debajo del campo, como el `hint` de los demás formularios. */
  hint: string;
}

/**
 * Modal para editar el RFC de un paso del onboarding, hermano de `EditPhoneDialog`:
 * es dueño del estado local del formulario y no conoce el API ni el store, solo
 * entrega el valor capturado vía `onSubmit`.
 */
export const EditRfcDialog = ({
  open,
  initialValue,
  onSubmit,
  onClose,
  disabled,
  maxLength,
  pattern,
  error,
  title,
  fieldLabel,
  hint,
}: Props) => {
  const [rfc, setRfc] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  // El error del API se oculta apenas el usuario corrige el valor, para no dejarlo en rojo mientras escribe.
  const [showApiError, setShowApiError] = useState(false);

  useEffect(() => {
    if (open) setRfc(initialValue);
  }, [open, initialValue]);

  useEffect(() => {
    setShowApiError(!!error);
  }, [error]);

  const isBlocked = disabled || submitting;
  const isEmpty = !rfc.trim();
  // El error solo se muestra cuando ya hay algo escrito, para no recibir al usuario con el campo en rojo.
  const formatError = !isEmpty && !pattern.test(rfc.trim()) ? "Formato incorrecto, inténtalo de nuevo" : "";
  const fieldError = formatError || (showApiError ? error : "");

  const handleSubmit = async () => {
    if (isBlocked || isEmpty || formatError) return;

    try {
      setSubmitting(true);
      await onSubmit(rfc.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ textAlign: "center", color: "primary.dark", position: "relative", pr: 6, pt: 3 }}>
        {title}
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "primary.dark" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Box sx={{ mt: 1, mb: 3 }}>
          <FieldWrapper label={fieldLabel} hint={hint} error={fieldError} full>
            <TextField
              size="lendia_input"
              id="rfc"
              name="rfc"
              fullWidth
              autoComplete="off"
              placeholder="ABC123456XYZ"
              onChange={(ev) => {
                setRfc(ev.target.value.toUpperCase());
                setShowApiError(false);
              }}
              value={rfc}
              inputProps={{ maxLength, style: { textTransform: "uppercase" }, "data-testid": "editar-rfc-input" }}
              disabled={isBlocked}
            />
          </FieldWrapper>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
          <Button tone="ghost" fullWidth disabled={isBlocked} onClick={onClose} data-testid="editar-rfc-cancel-button">
            Cancelar
          </Button>
          <Button
            fullWidth
            disabled={isBlocked || isEmpty || !!formatError}
            onClick={handleSubmit}
            data-testid="editar-rfc-submit-button"
          >
            Editar
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
